import type { ice } from '@halvard/rules';
import { SIDE_COLORS, WARRIOR_SCALE } from './look.js';

export interface WarriorModelProps {
  side: ice.Side;
  type: ice.WarriorType;
  /** Below 1 for ghosts and warriors going under. */
  opacity?: number;
  /** Which way it faces (radians, 0 = toward rank 1). Defaults to facing the enemy. */
  facing?: number;
}

const SKIN = '#E2C4A6';
const WOOD = '#7A5634';
const IRON = '#9AA3AB';

function Mat({ color, opacity }: { color: string; opacity: number }) {
  return (
    <meshStandardMaterial
      color={color}
      roughness={0.7}
      transparent={opacity < 1}
      opacity={opacity}
      depthWrite={opacity >= 1}
    />
  );
}

/**
 * A warrior facing the enemy (Brand looks toward rank 9, Rime toward rank 1). Bigger means
 * stronger: Skiers are small and on skis, Spears carry a shield and spear, Huscarls are broad,
 * with a big shield and an axe.
 */
export function WarriorModel({ side, type, opacity = 1, facing }: WarriorModelProps) {
  const { body, trim } = SIDE_COLORS[side];
  const scale = WARRIOR_SCALE[type];
  const broad = type === 'huscarl' ? 1.18 : 1;
  return (
    <group scale={scale} rotation={[0, facing ?? (side === 'brand' ? Math.PI : 0), 0]}>
      {/* Body and cloak. */}
      <mesh position={[0, 0.27, 0]} castShadow>
        <cylinderGeometry args={[0.14 * broad, 0.22 * broad, 0.5, 12]} />
        <Mat color={body} opacity={opacity} />
      </mesh>
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.17 * broad, 0.15 * broad, 0.06, 12]} />
        <Mat color={trim} opacity={opacity} />
      </mesh>
      {/* Head and helmet. */}
      <mesh position={[0, 0.64, 0]} castShadow>
        <sphereGeometry args={[0.1, 14, 10]} />
        <Mat color={SKIN} opacity={opacity} />
      </mesh>
      <mesh position={[0, 0.69, 0]} castShadow>
        <sphereGeometry args={[0.108, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <Mat color={IRON} opacity={opacity} />
      </mesh>
      <mesh position={[0, 0.66, 0.1]}>
        <boxGeometry args={[0.025, 0.09, 0.02]} />
        <Mat color={IRON} opacity={opacity} />
      </mesh>

      {type === 'skier' && (
        <group>
          {[-0.08, 0.08].map((x) => (
            <mesh key={x} position={[x, 0.015, 0.04]} castShadow>
              <boxGeometry args={[0.06, 0.02, 0.62]} />
              <Mat color={WOOD} opacity={opacity} />
            </mesh>
          ))}
          <mesh position={[0.2, 0.32, 0.02]} rotation={[0.2, 0, 0.15]}>
            <cylinderGeometry args={[0.012, 0.012, 0.6, 6]} />
            <Mat color={trim} opacity={opacity} />
          </mesh>
        </group>
      )}

      {type !== 'skier' && (
        // Round shield on the left arm, painted in the side's trim with an iron boss.
        <group position={[-0.2 * broad, 0.34, 0.1]} rotation={[0, -0.5, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry
              args={[type === 'huscarl' ? 0.24 : 0.19, type === 'huscarl' ? 0.24 : 0.19, 0.03, 20]}
            />
            <Mat color={trim} opacity={opacity} />
          </mesh>
          <mesh position={[0, 0, 0.02]}>
            <sphereGeometry args={[0.045, 10, 8]} />
            <Mat color={IRON} opacity={opacity} />
          </mesh>
        </group>
      )}

      {type === 'spear' && (
        <group position={[0.19, 0.45, 0.04]}>
          <mesh>
            <cylinderGeometry args={[0.014, 0.014, 0.85, 6]} />
            <Mat color={WOOD} opacity={opacity} />
          </mesh>
          <mesh position={[0, 0.48, 0]}>
            <coneGeometry args={[0.035, 0.13, 8]} />
            <Mat color={IRON} opacity={opacity} />
          </mesh>
        </group>
      )}

      {type === 'huscarl' && (
        <group position={[0.24, 0.4, 0.06]} rotation={[0.3, 0, -0.25]}>
          <mesh>
            <cylinderGeometry args={[0.017, 0.017, 0.62, 6]} />
            <Mat color={WOOD} opacity={opacity} />
          </mesh>
          <mesh position={[0.05, 0.27, 0]}>
            <boxGeometry args={[0.11, 0.12, 0.02]} />
            <Mat color={IRON} opacity={opacity} />
          </mesh>
        </group>
      )}
    </group>
  );
}
